import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DescriptionIcon from '@mui/icons-material/Description';
import Button from '@mui/material/Button';

import { QCReviewItem, QCReviewSubControl } from '../../../interfaces/qcReviewData.interface';
import { Comment } from '../../../interfaces/assessment.interface';
import {
  QCAdminResponse,
  QCReview,
  QCVerdict,
  VerificationStatus,
  QC_ADMIN_RESPONSE_LABELS,
  QC_FLAGGED_VERDICTS,
  VERIFICATION_STATUSES,
  VERIFICATION_STATUS_LABELS,
} from '../../../interfaces/qcReview.interface';
import Comments from '../../Comments/Comments';
import CommentBuilder from '../../Comments/CommentBuilder';
import FileViewer from '../../FileViewer/FileViewer';
import { getSPDisposition, isRealSubControl, isRollupReview } from './qcAttention';

const DISPOSITION_LABELS: Record<string, string> = {
  implemented: 'Implemented',
  not_implemented: 'Not Implemented',
  not_applicable: 'Not Applicable',
  compensating_control: 'Compensating Control',
};

const VERDICT_CONFIG: Record<QCVerdict, { label: string; color: 'default' | 'error' | 'warning'; variant: 'filled' | 'outlined'; needsHuman: boolean }> = {
  agree: { label: 'Agree', color: 'default', variant: 'outlined', needsHuman: false },
  disagree: { label: 'Disagree', color: 'error', variant: 'filled', needsHuman: false },
  uncertain: { label: 'Uncertain — needs human', color: 'warning', variant: 'outlined', needsHuman: true },
  not_evaluated: { label: 'Not evaluated — needs human', color: 'warning', variant: 'outlined', needsHuman: true },
};

// `confidence` isn't in the Bedrock tool schema's required fields, so the model can omit
// it (e.g. for a clear-cut case) — that comes back as null, not 0. Rendering `confidence
// || 0` would show "0%" as if the model reported near-zero confidence, so null must format
// as no percentage at all rather than coercing to zero.
function formatVerdictLabel(verdictLabel: string, confidence: number | null): string {
  if (confidence === null || confidence === undefined) return `QC: ${verdictLabel}`;
  return `QC: ${verdictLabel} (${Math.round(confidence * 100)}%)`;
}

const ADMIN_RESPONSE_OPTIONS: QCAdminResponse[] = ['accept', 'reject'];
const ADMIN_RESPONSE_COLORS: Record<QCAdminResponse, 'success' | 'inherit' | 'error'> = {
  accept: 'success',
  edit: 'inherit',
  reject: 'error',
};

// Per-QCReview accept/edit/reject control - the ADR-0005 agreement signal. Deliberately
// separate from the item-level "Your call on the assessor" disposition control below: this
// is the admin's reaction to ONE machine verdict, not to the assessor's answer (ADR-0007).
function AdminResponseControl({
  qcReview,
  canEdit,
  onSetAdminResponse,
}: {
  qcReview: QCReview;
  canEdit: boolean;
  onSetAdminResponse: (qcReviewId: number, response: QCAdminResponse) => Promise<void>;
}) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap' }}>
      <Tooltip title="Records whether the AI got this one right. It's just for tracking the AI's accuracy over time — it doesn't change the assessment, post anything, or take any action.">
        <Typography variant="caption" color="textSecondary" sx={{ textDecoration: 'underline dotted', cursor: 'help' }}>
          Was this QC finding useful?
        </Typography>
      </Tooltip>
      {ADMIN_RESPONSE_OPTIONS.map(option => (
        <Button
          key={option}
          size="small"
          variant={qcReview.adminResponse === option ? 'contained' : 'outlined'}
          color={ADMIN_RESPONSE_COLORS[option]}
          disabled={!canEdit}
          onClick={() => onSetAdminResponse(qcReview.id, option)}
          sx={{ minWidth: 0, px: 1.25, py: 0.25, fontSize: '0.75rem' }}
        >
          {QC_ADMIN_RESPONSE_LABELS[option]}
        </Button>
      ))}
    </Stack>
  );
}

interface SubControlRow {
  subControl: QCReviewSubControl;
  assessorDisposition?: string;
  spDisposition?: string;
  qcReview?: QCReview;
}

export interface ConsolidatedReviewViewProps {
  item: QCReviewItem;
  qcReviews: QCReview[];
  canEdit: boolean;
  onPostComment: (text: string, attachments: number[]) => Promise<void>;
  onEditComment: (commentId: number, text: string) => Promise<void>;
  onDeleteComment: (commentId: number) => Promise<void>;
  onSetVerificationStatus: (status: VerificationStatus) => Promise<void>;
  onSetAdminResponse: (qcReviewId: number, response: QCAdminResponse) => Promise<void>;
}

export default function ConsolidatedReviewView({
  item,
  qcReviews,
  canEdit,
  onPostComment,
  onEditComment,
  onDeleteComment,
  onSetVerificationStatus,
  onSetAdminResponse,
}: ConsolidatedReviewViewProps) {
  const [showComments, setShowComments] = useState(false);
  const [showDraftBuilder, setShowDraftBuilder] = useState(false);
  const [previewFile, setPreviewFile] = useState('');
  const [showFileViewer, setShowFileViewer] = useState(false);
  // Set when the draft builder was opened by clicking one finding's rationale text,
  // rather than the bulk "Edit AI Draft" button below - submitting in that case records
  // this one QCReview's admin_response as 'edit' automatically, no separate button needed.
  const [singleFindingDraft, setSingleFindingDraft] = useState<{ qcReviewId: number; text: string } | null>(null);

  const { question } = item;

  const subControlRows: SubControlRow[] = useMemo(() => {
    return question.subControls
      .filter(isRealSubControl)
      .map(subControl => {
        const assessorQuestionAnswer = item.assessorQuestionAnswers.find(aqa => aqa.answerId === subControl.id);
        // qcReviews is already scoped to this AssessorAnswer (assessorAnswerId filtered
        // upstream); answerId is the sub-control id, per qc/serializers.py.
        const qcReview = qcReviews.find(qc => qc.isCurrent && qc.answerId === subControl.id);

        return {
          subControl,
          assessorDisposition: assessorQuestionAnswer?.compensatingOrNotApplicable || undefined,
          spDisposition: getSPDisposition(item, subControl.id),
          qcReview,
        };
      });
  }, [item, qcReviews]);

  // "Look here first" set (qc/constants.py: QC_FLAGGED_VERDICTS) — not_evaluated is
  // "needs human" too, but it isn't counted as a flagged finding.
  const flaggedRows = subControlRows.filter(row => row.qcReview && QC_FLAGGED_VERDICTS.includes(row.qcReview.verdict));

  // The Question-level roll-up verdict (CONTEXT.md: "Line-items vs aggregate call") - a
  // persisted QCReview in its own right, with its own rationale, previously discarded
  // entirely. Normally at most one is current per item, but render defensively as a list.
  const rollupReviews = useMemo(
    () => qcReviews.filter(qc => qc.isCurrent && isRollupReview(qc)),
    [qcReviews],
  );

  // No disclaimer text baked in here — it must never be part of what could get posted
  // verbatim as a real comment. The "AI-generated" framing lives in a separate banner
  // rendered alongside CommentBuilder instead.
  const aiDraft = useMemo(() => {
    if (flaggedRows.length === 0) return '';
    const lines = flaggedRows.map(row =>
      `• ${row.subControl.text}\n  ${row.qcReview!.rationale}`,
    );
    return lines.join('\n\n');
  }, [flaggedRows]);

  const handleOpenEvidence = (documentUrl: string) => {
    setPreviewFile(documentUrl);
    setShowFileViewer(true);
  };

  const handleVerificationChange = (event: SelectChangeEvent) => {
    onSetVerificationStatus(event.target.value as VerificationStatus);
  };

  const handleSaveDraft = async (text: string) => {
    setShowDraftBuilder(false);
    const finishedFindingId = singleFindingDraft?.qcReviewId;
    setSingleFindingDraft(null);
    if (!text.trim()) return;
    await onPostComment(text, []);
    // The thread is collapsed by default on every question, so without this a post from the
    // draft builder lands with no visible feedback at all.
    setShowComments(true);
    if (finishedFindingId !== undefined) {
      await onSetAdminResponse(finishedFindingId, 'edit');
    }
  };

  const openDraftForFinding = (qcReview: QCReview, subControlText?: string) => {
    const text = subControlText ? `• ${subControlText}\n  ${qcReview.rationale}` : qcReview.rationale;
    setSingleFindingDraft({ qcReviewId: qcReview.id, text });
    setShowDraftBuilder(true);
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box>
            <Typography variant="h6">{question.title}</Typography>
            {question.description && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                {question.description}
              </Typography>
            )}
          </Box>
          {item.answer && <Chip label={item.answer} />}
        </Box>

        <Divider sx={{ my: 2 }} />

        {rollupReviews.length > 0 && (
          <>
            <Typography variant="subtitle2" gutterBottom>Question-Level Finding (Roll-up)</Typography>
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              {rollupReviews.map(qc => {
                const verdictConfig = VERDICT_CONFIG[qc.verdict];
                return (
                  <Box
                    key={qc.id}
                    sx={{
                      border: '1px solid',
                      borderColor: verdictConfig.color === 'error' ? 'error.main' : 'divider',
                      borderRadius: 1,
                      p: 1.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                      <Chip
                        size="small"
                        label={formatVerdictLabel(verdictConfig.label, qc.confidence)}
                        color={verdictConfig.color}
                        variant={verdictConfig.variant}
                      />
                      {qc.errorClass && qc.errorClass !== 'none' && (
                        <Chip size="small" label={qc.errorClass.replace(/_/g, ' ')} variant="outlined" />
                      )}
                      {qc.suspectedInjection && (
                        <Tooltip title="Suspected prompt injection in evidence — treat as needs-human, not a pass">
                          <Chip size="small" icon={<WarningAmberIcon />} label="Suspected injection" color="warning" />
                        </Tooltip>
                      )}
                    </Box>
                    <Tooltip title={canEdit ? 'Click to draft a comment from this finding' : ''}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ fontStyle: 'italic', cursor: canEdit ? 'pointer' : 'default' }}
                        onClick={() => canEdit && openDraftForFinding(qc)}
                      >
                        {qc.rationale}
                      </Typography>
                    </Tooltip>
                    <AdminResponseControl qcReview={qc} canEdit={canEdit} onSetAdminResponse={onSetAdminResponse} />
                  </Box>
                );
              })}
            </Stack>
            <Divider sx={{ my: 2 }} />
          </>
        )}

        <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
          <Box sx={{ flex: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Assessor Finding</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {item.assessorFinding || '—'}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Flagged Sub-controls</Typography>
            <Typography variant="body2">
              {flaggedRows.length} of {subControlRows.length} sub-controls flagged for review
            </Typography>
          </Box>
        </Box>

        {item.evidence.length > 0 && (
          <>
            <Typography variant="subtitle2" gutterBottom>Evidence</Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
              {item.evidence.map(ev => (
                <Chip
                  key={ev.id}
                  icon={<DescriptionIcon />}
                  label={ev.document.split(/[#?]/)[0].split('/').pop()}
                  onClick={() => handleOpenEvidence(ev.document)}
                  variant="outlined"
                />
              ))}
            </Stack>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>Sub-controls</Typography>
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {subControlRows.map(row => {
            const diverges = row.spDisposition && row.assessorDisposition && row.spDisposition !== row.assessorDisposition;
            const verdictConfig = row.qcReview ? VERDICT_CONFIG[row.qcReview.verdict] : undefined;

            return (
              <Box
                key={row.subControl.id}
                sx={{
                  border: '1px solid',
                  borderColor: verdictConfig?.color === 'error' ? 'error.main' : 'divider',
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {row.subControl.text}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: row.qcReview ? 1 : 0 }}>
                  <Typography variant="caption" color="textSecondary">
                    SP: {row.spDisposition ? DISPOSITION_LABELS[row.spDisposition] : 'Not Answered'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: diverges ? 'error.main' : 'text.secondary', fontWeight: diverges ? 'bold' : 'normal' }}
                  >
                    Assessor: {row.assessorDisposition ? DISPOSITION_LABELS[row.assessorDisposition] : 'Not Answered'}
                    {diverges ? ' (diverges from SP)' : ''}
                  </Typography>
                  {verdictConfig && (
                    <Chip
                      size="small"
                      label={formatVerdictLabel(verdictConfig.label, row.qcReview!.confidence)}
                      color={verdictConfig.color}
                      variant={verdictConfig.variant}
                    />
                  )}
                  {row.qcReview?.errorClass && row.qcReview.errorClass !== 'none' && (
                    <Chip size="small" label={row.qcReview.errorClass.replace(/_/g, ' ')} variant="outlined" />
                  )}
                  {row.qcReview?.suspectedInjection && (
                    <Tooltip title="Suspected prompt injection in evidence — treat as needs-human, not a pass">
                      <Chip size="small" icon={<WarningAmberIcon />} label="Suspected injection" color="warning" />
                    </Tooltip>
                  )}
                </Box>
                {row.qcReview && (
                  <>
                    <Tooltip title={canEdit ? 'Click to draft a comment from this finding' : ''}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ fontStyle: 'italic', cursor: canEdit ? 'pointer' : 'default' }}
                        onClick={() => canEdit && openDraftForFinding(row.qcReview!, row.subControl.text)}
                      >
                        {row.qcReview.rationale}
                      </Typography>
                    </Tooltip>
                    <AdminResponseControl qcReview={row.qcReview} canEdit={canEdit} onSetAdminResponse={onSetAdminResponse} />
                  </>
                )}
              </Box>
            );
          })}
        </Stack>

        <FileViewer
          previewFile={previewFile}
          canDownload
          showFileViewer={showFileViewer}
          closeFileViewer={() => setShowFileViewer(false)}
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>Comments</Typography>
        {showComments ? (
          <Comments
            disabled={!canEdit}
            comments={item.comments as unknown as Comment[]}
            handleClose={() => setShowComments(false)}
            postComment={onPostComment}
            onEditComment={onEditComment}
            onDeleteComment={onDeleteComment}
          />
        ) : (
          <Button size="small" onClick={() => setShowComments(true)}>Show comment thread</Button>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Tooltip title={flaggedRows.length === 0 ? 'No sub-controls were flagged for this question' : ''}>
            <span>
              <Button
                variant="outlined"
                disabled={!canEdit || flaggedRows.length === 0}
                onClick={() => {
                  setSingleFindingDraft(null);
                  setShowDraftBuilder(true);
                }}
              >
                Edit AI Draft & Post Comment
              </Button>
            </span>
          </Tooltip>

          <Box>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id={`disposition-${question.id}`}>Your call on the assessor</InputLabel>
              <Select
                labelId={`disposition-${question.id}`}
                label="Your call on the assessor"
                value={item.verificationStatus || VERIFICATION_STATUSES.NOT_STARTED}
                onChange={handleVerificationChange}
                disabled={!canEdit}
              >
                {Object.values(VERIFICATION_STATUSES).map(status => (
                  <MenuItem key={status} value={status}>{VERIFICATION_STATUS_LABELS[status]}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, maxWidth: 260 }}>
              Was the assessor right? This is separate from responding to individual QC
              findings above.
            </Typography>
          </Box>
        </Box>

        <CommentBuilder
          canEdit={canEdit}
          commentBuilderDefault={singleFindingDraft?.text ?? aiDraft}
          banner={
            <Alert severity="info" sx={{ mt: 2 }}>
              AI-generated draft — review and edit before posting. <strong>Post Comment</strong> adds
              this to the comment thread on this question right away, where the assessor can see it.
            </Alert>
          }
          dialogTitle="Admin Comment to Assessor"
          fieldLabel="Comment to Assessor"
          // Not the shared "Continue" default: unlike AssessorPIDetails, which only writes the
          // draft back to a local form field, saving here posts to the thread immediately.
          saveLabel="Post Comment"
          showCommentBuilder={showDraftBuilder}
          handleHideCommentBuilder={() => setShowDraftBuilder(false)}
          handleSaveCommentBuilder={handleSaveDraft}
        />
      </CardContent>
    </Card>
  );
}
